import React, {useMemo, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {Breadcrumbs, Button, Link, SearchField, Table, TableBar, Tabs, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import Styled from 'styled-components';
import {SettingsFinanceBillTo_viewer} from './__generated__/SettingsFinanceBillTo_viewer.graphql';
import {SIZE} from '@/web-components';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import Util from '../../../shared/util/util';
import CustomScrollDiv from '../../../shared/components/scroll-bars/custom_scroll_div';
import {INVOICE_INTEGRATIONS} from './InvoiceIntegrationsUtils';
import InvoicingIntegrationIcon from './InvocingIntegrationIcon';

const PageWrapper = Styled.div`
  padding: 32px;
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const BreadcrumbsWrapper = Styled.div`
  padding-bottom: 24px;
 `;

type SettingsFinanceBillToProps = {
	viewer: SettingsFinanceBillTo_viewer;
};

type BillFrom = {
	id: string;
	name: string;
	integration: string;
	billTosCount: number;
	billTos: {edges: {node: {id: string}}[]};
};

const sortedBillFromArray = (viewer: SettingsFinanceBillTo_viewer) => {
	const org = viewer.company?.billFroms;
	const array: BillFrom[] = Array.isArray(org) ? [...org] : [];
	const integrationSortOrder = Object.keys(INVOICE_INTEGRATIONS);
	array.sort((a, b) => {
		const intIndexA = integrationSortOrder.indexOf(a.integration);
		const intIndexB = integrationSortOrder.indexOf(b.integration);
		if (intIndexA === intIndexB) {
			return a.name.localeCompare(b.name);
		} else {
			return intIndexA > intIndexB ? 1 : -1;
		}
	});
	return array;
};

const getAssociatedClients = (viewer: SettingsFinanceBillTo_viewer, billToId: string) =>
	viewer.company?.clients?.edges
		?.filter(edge =>
			edge?.node?.clientBillTos?.edges?.some(clientBillToEdge => {
				return clientBillToEdge?.node?.billTo?.id === billToId;
			})
		)
		.map(edge => {
			const client = edge?.node;
			const invoiced =
				client?.clientBillTos?.edges?.some(clientBillToEdge => {
					const clientBillTo = clientBillToEdge?.node;
					return clientBillTo?.billTo?.id === billToId && clientBillTo?.invoiced;
				}) || false;
			return {
				id: client?.id,
				name: client?.name,
				invoiced,
			};
		});

const mappedBillTos = (viewer: SettingsFinanceBillTo_viewer, billFromId: string) =>
	viewer.company?.billFroms
		?.find(billFrom => billFrom?.id === billFromId)
		?.billTos?.edges?.map(edge => ({clients: getAssociatedClients(viewer, edge?.node?.id || ''), ...edge?.node}));

const SettingsFinanceBillTo = ({viewer}: SettingsFinanceBillToProps) => {
	const {formatMessage} = useIntl();
	const billFroms = useMemo(() => sortedBillFromArray(viewer), [viewer]);
	const [billFromId, setBillFromId] = useState(billFroms[0]?.id || '');
	const billFromIntegrationName = billFroms.find(billFrom => billFrom.id === billFromId)?.integration;
	const billTos = useMemo(() => mappedBillTos(viewer, billFromId), [viewer, billFromId]);
	const [searchFilterValue, setSearchFilterValue] = useState('');

	const openModal = () => {
		showModal({
			type: MODAL_TYPE.ADD_BILL_TO_ENTITY,
			billFromId,
		});
	};
	const removeBillTo = row => {
		showModal({
			type: row.invoiced ? MODAL_TYPE.INVOICED_BILL_TO_WARNING : MODAL_TYPE.REMOVE_BILL_TO,
			billToId: row.id,
			billFromId,
		});
	};
	const associateBillTo = row => {
		showModal({
			type: MODAL_TYPE.ASSOCIATE_BILL_TO,
			billToId: row.id,
			billFromId,
		});
	};
	const disassociateBillTo = row => {
		if (row.clients.length === 1 && row.clients[0].invoiced) {
			showModal({
				type: MODAL_TYPE.INVOICED_BILL_TO_WARNING,
				billToId: row.id,
				clientId: row.clients[0].id,
			});
		} else {
			showModal({
				type: MODAL_TYPE.UNASSOCIATE_BILL_TO,
				billToId: row.id,
				billFromId,
			});
		}
	};

	return (
		<PageWrapper>
			<BreadcrumbsWrapper>
				<Breadcrumbs>
					<Breadcrumbs.Item>
						<Link href={'/admin/finance#invoicing'}>
							<FormattedMessage id="settings_finance.menuTitle" />
						</Link>
					</Breadcrumbs.Item>
					<Breadcrumbs.Item>
						<Text>{formatMessage({id: 'settings_finance.manage_bill_to_entities'})}</Text>
					</Breadcrumbs.Item>
				</Breadcrumbs>
			</BreadcrumbsWrapper>
			<TableBar title={formatMessage({id: 'settings_finance.manage_bill_to_entities'})}>
				<SearchField id={TableBar.ELEMENTS.SEARCH} value={searchFilterValue} onChange={setSearchFilterValue} />
				<Button onPress={openModal}>
					<FormattedMessage id="settings_finance.add_bill_to_entity" />
				</Button>
				<Tabs id={TableBar.ELEMENTS.TABS} value={billFromId} onValueChange={setBillFromId}>
					<Tabs.List>
						{billFroms.map(billFrom => (
							<Tabs.Tab key={billFrom.id} value={billFrom.id}>
								<InvoicingIntegrationIcon integration={billFrom.integration} size={SIZE.TAB} />
								{billFrom.name} ({billFrom.billTos.edges.length})
							</Tabs.Tab>
						))}
					</Tabs.List>
				</Tabs>
			</TableBar>
			{!!billTos && (
				<CustomScrollDiv autoHide={false} style={{flexGrow: 1}}>
					<Table data={billTos} searchValue={searchFilterValue} width={'100%'}>
						<Table.TextColumn
							accessorKey={'name'}
							header={formatMessage({id: 'settings_finance.bill_to_entity_name'})}
							allowSorting
						/>
						<Table.TextColumn
							accessorKey={'address'}
							header={formatMessage({id: 'settings_finance.address'})}
							allowSorting
						/>
						{Util.shouldDisplayTaxIdColumn(billFromIntegrationName) && (
							<Table.TextColumn
								accessorKey={'taxId'}
								header={formatMessage({id: 'settings.client.vat_number'})}
								allowSorting
							/>
						)}
						<Table.ArrayColumn
							accessorKey={'clients'}
							tooltipRender={values => values.map(value => value.name).join('\n')}
							render={values => (values.length ? values[0].name : 'None')}
							renderLimit={1}
							renderLimited={count => `Multiple clients (${count})`}
							header={formatMessage({id: 'settings_finance.associated_clients'})}
							allowSorting
						/>
						<Table.ActionColumn header={'Actions'} hideHeader>
							<Table.ActionColumn.Option onClick={associateBillTo}>
								<FormattedMessage id={'settings_finance.bill_to.associate_client'} />
							</Table.ActionColumn.Option>
							<Table.ActionColumn.Option onClick={disassociateBillTo}>
								<FormattedMessage id={'settings_finance.bill_to.disassociate_client'} />
							</Table.ActionColumn.Option>
							<Table.ActionColumn.Option onClick={removeBillTo}>
								<FormattedMessage id={'settings_finance.bill_to.remove'} />
							</Table.ActionColumn.Option>
						</Table.ActionColumn>
					</Table>
				</CustomScrollDiv>
			)}
		</PageWrapper>
	);
};

export const SettingsFinanceBillToQuery = graphql`
	query SettingsFinanceBillTo_Query {
		viewer {
			actualPersonId
			component(name: "settings_finance")
			...SettingsFinanceBillTo_viewer
		}
	}
`;

export default createFragmentContainer(SettingsFinanceBillTo, {
	viewer: graphql`
		fragment SettingsFinanceBillTo_viewer on Viewer {
			id
			company {
				id
				billFroms {
					id
					integration
					name
					billTos(first: 10000) @connection(key: "BillFrom_billTos", filters: []) {
						edges {
							node {
								id
								name
								address
								taxId
								invoiced
							}
						}
					}
				}
				clients(first: 100000) {
					edges {
						node {
							id
							name
							clientBillTos(first: 10000) @connection(key: "Client_clientBillTos", filters: []) {
								edges {
									node {
										id
										billFrom {
											id
										}
										billTo {
											id
										}
										clientId
										invoiced
									}
								}
							}
						}
					}
				}
			}
		}
	`,
});
